import type { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import {
  endOfLocalDay,
  parseLocalDateParam,
  startOfLocalDay,
  toDateParam,
} from "~/shared/utils/filterDateFormatter";

const GPS_TRACKING_PATH = "/gps-tracking";

/** What the screen keeps in its URL, so a link opens exactly the same view. */
export type GpsTrackingUrlState = {
  /** The day on screen, at local midnight: the screen shows one whole day. */
  day: Date;
  truckId?: string;
  /** The playback moment, ms since the epoch. */
  time?: number;
};

const PARAMS = { date: "date", truckId: "truckId", time: "time" } as const;

/**
 * The period a day stands for: midnight to its last millisecond. Today runs to its end too,
 * not to "now": a fixed end keeps the requests' arguments, and so their cache entries, from
 * moving while the screen is open.
 */
export function dayPeriod(day: Date): { start: Date; end: Date } {
  return { start: startOfLocalDay(day), end: endOfLocalDay(day) };
}

/**
 * Reads the screen's state from its URL. Anything unusable — an unreadable or future day, a
 * malformed truck id, a playback moment outside the day — is corrected rather than rejected;
 * `corrected` says the URL should be rewritten to match.
 */
export function parseGpsTrackingParams(
  params: URLSearchParams,
  now: Date,
): { state: GpsTrackingUrlState; corrected: boolean } {
  let corrected = false;
  const today = startOfLocalDay(now);

  const rawDate = params.get(PARAMS.date);
  let day = parseLocalDateParam(rawDate ?? undefined);
  if (!day || day > today) day = today;
  // Also rewrites a date given in another readable form to YYYY-MM-DD.
  corrected ||= rawDate !== null && rawDate !== toDateParam(day);

  const rawTruckId = params.get(PARAMS.truckId);
  const truckId = rawTruckId && /^[1-9]\d*$/.test(rawTruckId) ? rawTruckId : undefined;
  corrected ||= rawTruckId !== null && !truckId;

  const { start, end } = dayPeriod(day);
  const rawTime = params.get(PARAMS.time);
  const parsedTime = rawTime === null ? NaN : Number(rawTime);
  const time =
    truckId &&
    Number.isInteger(parsedTime) &&
    parsedTime >= start.getTime() &&
    parsedTime <= end.getTime()
      ? parsedTime
      : undefined;
  corrected ||= rawTime !== null && time === undefined;

  return { state: { day, truckId, time }, corrected };
}

/**
 * The URL query for a state. A missing day is left out, which the screen reads as today. A
 * playback moment needs a truck to play.
 */
export function gpsTrackingSearch(state: Partial<GpsTrackingUrlState>): string {
  const params = new URLSearchParams();
  if (state.day) params.set(PARAMS.date, toDateParam(state.day));
  if (state.truckId) {
    params.set(PARAMS.truckId, state.truckId);
    if (state.time !== undefined) params.set(PARAMS.time, String(Math.round(state.time)));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function gpsTrackingUrl(state: Partial<GpsTrackingUrlState>): string {
  return `${GPS_TRACKING_PATH}${gpsTrackingSearch(state)}`;
}

/**
 * Where a waste-export job is checked against the track: its truck on the job's day, the
 * player at the moment the job's service started. Undefined while the job has no truck.
 */
export function jobGpsTrackingUrl(
  job: Pick<ConnectProto.Job, "driverOnShift" | "factStartAt" | "planStartAt">,
): string | undefined {
  const truckId = job.driverOnShift?.truck?.id ?? job.driverOnShift?.truckId;
  if (!truckId) return undefined;

  const started = fromTimestamp(job.factStartAt);
  const day = started ?? fromTimestamp(job.planStartAt);

  return gpsTrackingUrl({
    day: day ?? undefined,
    truckId: String(truckId),
    time: started?.getTime(),
  });
}
